// Расходы заведения: любые траты со статьёй и способом оплаты.
//
// Способ решает, попадёт ли расход в кассовую смену: «С кассы (наличные)»
// уменьшает ожидаемую наличность ящика, «С кассы (перевод)» виден в отчёте
// смены, остальные — общий расход заведения. И то, и другое учитывается
// в аналитике за период.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banknote, CreditCard, Edit, History, Lock, Plus, Receipt, Search, Trash2, Wallet, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { NumberInput } from '@/components/ui/NumberInput';
import { Spinner } from '@/components/ui/Spinner';
import {
  createExpense, createExpenseCategory, deleteExpense, deleteExpenseCategory,
  getCurrentShift, listExpenseCategories, listExpenseMethodLabels, listExpenses, listShifts,
  seedExpenseCategories, updateExpense, updateExpenseCategory,
  EXPENSE_METHOD_LABELS, isKassaExpense,
  type Expense, type ExpenseCategory, type ExpenseMethod,
} from '@/services/admin';
import { formatDate, formatRub } from '@/utils/format';

type Tab = 'list' | 'categories';

const METHODS: ExpenseMethod[] = ['KASSA_CASH', 'KASSA_TRANSFER', 'CASH', 'CARD', 'OTHER'];

const methodTone: Record<ExpenseMethod, 'success' | 'info' | 'warning' | 'slate'> = {
  KASSA_CASH: 'success',
  KASSA_TRANSFER: 'info',
  CASH: 'warning',
  CARD: 'info',
  OTHER: 'slate',
};

const methodShort: Record<ExpenseMethod, string> = {
  KASSA_CASH: 'Касса · наличные',
  KASSA_TRANSFER: 'Касса · перевод',
  CASH: 'Наличные',
  CARD: 'Карта',
  OTHER: 'Другой',
};

// Палитра для бейджей статей — те же тона, что и в остальной админке.
const CATEGORY_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#8b5cf6', '#ec4899', '#64748b',
];

const dateKey = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dateKey(d);
};
const monthStart = () => {
  const d = new Date();
  return dateKey(new Date(d.getFullYear(), d.getMonth(), 1));
};

type Preset = 'today' | '7d' | '30d' | 'month' | 'all';
const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: 'today', label: 'Сегодня' },
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
  { id: 'month', label: 'Месяц' },
  { id: 'all', label: 'Всё время' },
];

function presetRange(p: Preset): { from?: string; to?: string } {
  const today = dateKey(new Date());
  if (p === 'today') return { from: today, to: today };
  if (p === '7d') return { from: daysAgo(6), to: today };
  if (p === '30d') return { from: daysAgo(29), to: today };
  if (p === 'month') return { from: monthStart(), to: today };
  return {};
}

export default function ExpensesPage() {
  const [tab, setTab] = useState<Tab>('list');

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Расходы</h1>
          <p className="text-sm text-slate-500">
            Траты заведения по статьям. Оплата «с кассы» уменьшает кассу смены
          </p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-full sm:w-auto">
          <TabBtn active={tab === 'list'} onClick={() => setTab('list')}>Расходы</TabBtn>
          <TabBtn active={tab === 'categories'} onClick={() => setTab('categories')}>Статьи</TabBtn>
        </div>
      </header>

      {tab === 'list' ? <ExpensesTab /> : <CategoriesTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap shrink-0 ${active ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
    >
      {children}
    </button>
  );
}

// ─── Список расходов ──────────────────────────────────────────────────
function ExpensesTab() {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<Preset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [method, setMethod] = useState<'' | ExpenseMethod>('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<Expense | 'new' | null>(null);

  const useCustom = Boolean(customFrom || customTo);
  const range = useCustom ? { from: customFrom || undefined, to: customTo || undefined } : presetRange(preset);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/expenses', range.from ?? '', range.to ?? '', categoryId, method, search],
    queryFn: () =>
      listExpenses({
        ...range,
        categoryId: categoryId || undefined,
        method: method || undefined,
        q: search.trim() || undefined,
        limit: 300,
      }),
  });
  const { data: categories } = useQuery({
    queryKey: ['admin/expense-categories'],
    queryFn: () => listExpenseCategories(),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteExpense(id),
    onSuccess: () => {
      toast.success('Расход удалён');
      qc.invalidateQueries({ queryKey: ['admin/expenses'] });
      qc.invalidateQueries({ queryKey: ['admin/current-shift'] });
      qc.invalidateQueries({ queryKey: ['admin/analytics'] });
    },
  });

  const summary = data?.summary;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setPreset(p.id);
                setCustomFrom('');
                setCustomTo('');
              }}
              className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap ${
                !useCustom && preset === p.id ? 'bg-white shadow text-brand-700' : 'text-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button className="btn-primary" onClick={() => setForm('new')}>
          <Plus size={16} /> Добавить расход
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <SumCard title="Всего за период" value={summary?.total ?? 0} icon={<Receipt size={18} />} tone="brand" />
        <SumCard title="Наличными из кассы" value={summary?.kassaCash ?? 0} icon={<Banknote size={18} />} tone="amber" />
        <SumCard title="Прочие способы" value={summary?.other ?? 0} icon={<CreditCard size={18} />} tone="slate" />
        <div className="card p-4">
          <div className="text-xs text-slate-500 mb-1">Записей</div>
          <div className="text-xl font-semibold tabular-nums">{summary?.count ?? 0}</div>
        </div>
      </div>

      <div className="card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[150px]">
          <label className="label">Статья</label>
          <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Все статьи</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[150px]">
          <label className="label">Способ</label>
          <select
            className="input"
            value={method}
            onChange={(e) => setMethod(e.target.value as '' | ExpenseMethod)}
          >
            <option value="">Любой</option>
            {METHODS.map((m) => (
              <option key={m} value={m}>{EXPENSE_METHOD_LABELS[m]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">С даты</label>
          <input type="date" className="input" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">По дату</label>
          <input type="date" className="input" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="label">Поиск по комментарию</label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Например: такси"
            />
          </div>
        </div>
        {(useCustom || categoryId || method || search) && (
          <button
            className="btn-ghost text-sm"
            onClick={() => {
              setCustomFrom('');
              setCustomTo('');
              setCategoryId('');
              setMethod('');
              setSearch('');
            }}
          >
            <X size={15} /> Сбросить
          </button>
        )}
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Дата</th>
                <th className="text-left px-4 py-3">Статья</th>
                <th className="text-left px-4 py-3">Комментарий</th>
                <th className="text-left px-4 py-3">Способ</th>
                <th className="text-left px-4 py-3">Смена</th>
                <th className="text-right px-4 py-3">Сумма</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((e) => (
                <tr key={e.id} className="table-row">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    {formatDate(e.spentAt, { hour: undefined, minute: undefined })}
                    <div className="text-xs text-slate-400">{e.user?.name ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <CategoryTag category={e.category} />
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[280px] truncate" title={e.comment ?? ''}>
                    {e.comment ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block max-w-[170px]" title={e.methodLabel ?? undefined}>
                      <Badge tone={methodTone[e.method]}>
                        <span className="truncate">
                          {e.method === 'OTHER' ? e.methodLabel ?? 'Другой' : methodShort[e.method]}
                        </span>
                      </Badge>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {e.shift ? (
                      <span className="inline-flex items-center gap-1">
                        №{e.shift.number}
                        {e.locked && <Lock size={12} className="text-slate-400" />}
                        {e.addedAfterClose && (
                          <span title="Внесён после закрытия смены">
                            <Badge tone="warning">после закрытия</Badge>
                          </span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums whitespace-nowrap">
                    {formatRub(e.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button className="btn-ghost p-1.5" title="Изменить" onClick={() => setForm(e)}>
                        <Edit size={15} />
                      </button>
                      <button
                        className="btn-ghost p-1.5 text-rose-600 disabled:opacity-40"
                        title={e.locked ? 'Смена закрыта — расход не удалить' : 'Удалить'}
                        disabled={e.locked || delMut.isPending}
                        onClick={() => {
                          if (confirm(`Удалить расход на ${formatRub(e.amount)}?`)) delMut.mutate(e.id);
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-400 py-10">
                    За выбранный период расходов нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {summary && summary.byCategory.length > 0 && (
        <div className="card p-5 mt-4">
          <div className="text-sm font-medium mb-3">По статьям за период</div>
          <div className="grid gap-2">
            {summary.byCategory.map((c) => {
              const share = summary.total > 0 ? Math.round((c.total / summary.total) * 100) : 0;
              return (
                <div key={c.id ?? 'none'} className="flex items-center gap-3">
                  <div className="w-40 shrink-0 truncate text-sm text-slate-600">{c.name}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${share}%`, background: c.color ?? '#f97316' }}
                    />
                  </div>
                  <div className="w-28 text-right text-sm tabular-nums font-medium">{formatRub(c.total)}</div>
                  <div className="w-10 text-right text-xs text-slate-400 tabular-nums">{share}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {form && (
        <ExpenseModal
          expense={form === 'new' ? null : form}
          categories={categories ?? []}
          onClose={() => setForm(null)}
        />
      )}
    </>
  );
}

function SumCard({
  title,
  value,
  icon,
  tone,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  tone: 'brand' | 'amber' | 'slate';
}) {
  const tones = {
    brand: 'bg-brand-50 text-brand-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-500',
  } as const;
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 mb-1 truncate">{title}</div>
          <div className="text-xl font-semibold tabular-nums">{formatRub(value)}</div>
        </div>
        <span className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${tones[tone]}`}>{icon}</span>
      </div>
    </div>
  );
}

function CategoryTag({ category }: { category: { name: string; color: string | null } | null }) {
  if (!category) return <span className="text-slate-400">Без статьи</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-700">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: category.color ?? '#cbd5e1' }}
      />
      {category.name}
    </span>
  );
}

// ─── Форма расхода ────────────────────────────────────────────────────
const NEW_CATEGORY = '__new__';

type ShiftRef = { id: string; number: number; status: 'OPEN' | 'CLOSED'; closedAt?: string | null };

function diffLabel(diff: number | null | undefined): string {
  if (diff == null) return '';
  if (diff === 0) return ' · сошлось';
  return diff < 0 ? ` · недостача ${formatRub(-diff)}` : ` · излишек ${formatRub(diff)}`;
}

function formatSignedRub(kop: number): string {
  if (kop === 0) return '0 ₽';
  return `${kop > 0 ? '+' : '−'}${formatRub(Math.abs(kop))}`;
}

/**
 * Расхождение закрытой смены после этого расхода: ожидание в ящике падает на
 * сумму. При правке расхода, уже лежащего в этой смене наличными, его прежняя
 * сумма в расхождении уже учтена.
 */
function projectedDiff(
  shift: { id: string; cashDiff?: number | null },
  amountKop: number,
  expense: Expense | null,
): number {
  const counted = expense && expense.method === 'KASSA_CASH' && expense.shift?.id === shift.id
    ? expense.amount
    : 0;
  return (shift.cashDiff ?? 0) + amountKop - counted;
}

export function ExpenseModal({
  expense,
  categories,
  onClose,
  presetShift,
}: {
  expense: Expense | null;
  categories: ExpenseCategory[];
  onClose: () => void;
  /** Расход сразу в эту смену — из отчёта смены («вычет с закрытой кассы»). */
  presetShift?: ShiftRef;
}) {
  const qc = useQueryClient();
  const { data: current } = useQuery({ queryKey: ['admin/current-shift'], queryFn: getCurrentShift });
  const { data: labels } = useQuery({
    queryKey: ['admin/expense-method-labels'],
    queryFn: listExpenseMethodLabels,
  });
  // Последние закрытые смены — для расхода, который забыли провести до закрытия.
  const { data: closedShifts = [] } = useQuery({
    queryKey: ['admin/shifts', 'closed-for-expense'],
    queryFn: () => listShifts({ status: 'CLOSED', limit: 30 }),
    staleTime: 60_000,
  });

  const [amountRub, setAmountRub] = useState(expense ? expense.amount / 100 : 0);
  const [method, setMethod] = useState<ExpenseMethod>(expense?.method ?? 'KASSA_CASH');
  const [methodLabel, setMethodLabel] = useState(expense?.methodLabel ?? '');
  const [categoryId, setCategoryId] = useState(expense?.category?.id ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [comment, setComment] = useState(expense?.comment ?? '');
  const [spentAt, setSpentAt] = useState(
    expense ? expense.spentAt.slice(0, 10) : dateKey(new Date()),
  );
  // '' — текущая открытая смена, иначе id закрытой смены.
  const [shiftId, setShiftId] = useState<string>(() => {
    const ref = presetShift ?? expense?.shift ?? null;
    return ref && ref.status === 'CLOSED' ? ref.id : '';
  });

  const hasOpenShift = !!current?.shift;
  const kassa = isKassaExpense(method);
  const cashInDrawer = current?.shift?.expectedCash ?? 0;
  // При правке уже проведённого расхода его сумма уже вычтена из ящика.
  const alreadyCounted = expense && expense.method === 'KASSA_CASH' ? expense.amount : 0;
  const amountKop = Math.round(amountRub * 100);
  const overdrawn = amountKop - alreadyCounted > cashInDrawer;

  // Закрытые смены для выбора + та, к которой расход уже привязан (если она
  // старше последних 30).
  const closedOptions = useMemo(() => {
    const list: Array<ShiftRef & { cashDiff?: number | null }> = [...closedShifts];
    for (const ref of [presetShift, expense?.shift]) {
      if (ref && ref.status === 'CLOSED' && !list.some((s) => s.id === ref.id)) list.push(ref);
    }
    return list;
  }, [closedShifts, presetShift, expense?.shift]);
  const pickedClosed = shiftId ? closedOptions.find((s) => s.id === shiftId) ?? null : null;

  // Данные о смене приходят асинхронно: если смены нет, уводим новый расход
  // с кассового способа на «наличные» — это обычно общий расход (аренда, такси).
  const methodTouched = useRef(false);
  useEffect(() => {
    if (expense || presetShift || methodTouched.current || !current) return;
    if (!current.shift && isKassaExpense(method) && !shiftId) setMethod('CASH');
  }, [current, expense, presetShift, method, shiftId]);
  const locked = expense?.locked ?? false;
  // Способ и сумму у расхода из закрытой смены править нельзя — иначе
  // разъедется уже сданный Z-отчёт. Статья и комментарий остаются доступны.
  const moneyDisabled = locked;
  // Кассовые способы недоступны, только если выбрать вообще нечего.
  const kassaUnavailable = !hasOpenShift && closedOptions.length === 0;

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!(amountRub > 0)) e.push('Укажите сумму больше 0');
    if (method === 'OTHER' && !methodLabel.trim()) e.push('Впишите название способа оплаты');
    if (categoryId === NEW_CATEGORY && !newCategory.trim()) e.push('Впишите название новой статьи');
    if (kassa && !locked && !shiftId && !hasOpenShift) {
      e.push('Открытой смены нет — выберите закрытую смену');
    }
    return e;
  }, [amountRub, method, methodLabel, categoryId, newCategory, kassa, locked, shiftId, hasOpenShift]);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        amount: amountKop,
        method,
        methodLabel: method === 'OTHER' ? methodLabel.trim() : null,
        categoryId: categoryId && categoryId !== NEW_CATEGORY ? categoryId : null,
        categoryName: categoryId === NEW_CATEGORY ? newCategory.trim() : null,
        comment: comment.trim() || null,
        spentAt: kassa ? null : new Date(`${spentAt}T12:00:00`).toISOString(),
        // Явный id смены: иначе поправку из закрытой смены нельзя было бы
        // перенести обратно в открытую.
        shiftId: kassa && !locked ? shiftId || current?.shift?.id || null : null,
      };
      return expense ? updateExpense(expense.id, payload) : createExpense(payload);
    },
    onSuccess: () => {
      toast.success(expense ? 'Расход обновлён' : 'Расход добавлен');
      qc.invalidateQueries({ queryKey: ['admin/expenses'] });
      qc.invalidateQueries({ queryKey: ['admin/expense-categories'] });
      qc.invalidateQueries({ queryKey: ['admin/expense-method-labels'] });
      qc.invalidateQueries({ queryKey: ['admin/current-shift'] });
      qc.invalidateQueries({ queryKey: ['admin/analytics'] });
      qc.invalidateQueries({ queryKey: ['admin/shift'] });
      qc.invalidateQueries({ queryKey: ['admin/shifts'] });
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Не удалось сохранить расход'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={expense ? 'Изменить расход' : 'Новый расход'}
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn-primary"
            disabled={errors.length > 0 || saveMut.isPending}
            onClick={() => {
              if (errors.length) { toast.error(errors[0]); return; }
              saveMut.mutate();
            }}
          >
            {saveMut.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        {locked && (
          <div className="flex gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-600">
            <Lock size={14} className="mt-0.5 shrink-0 text-slate-400" />
            Расход проведён по закрытой смене №{expense?.shift?.number}. Сумму, способ и дату
            менять нельзя — только статью и комментарий.
          </div>
        )}
        {expense?.addedAfterClose && (
          <div className="flex gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
            <History size={14} className="mt-0.5 shrink-0" />
            Расход внесён после закрытия смены №{expense.shift?.number} — это поправка к её отчёту,
            её можно исправить или удалить.
          </div>
        )}

        <div>
          <label className="label">Сумма, ₽</label>
          <NumberInput
            className="input text-lg font-semibold tabular-nums"
            value={amountRub}
            onChange={setAmountRub}
            disabled={moneyDisabled}
            autoFocus
          />
        </div>

        <div>
          <label className="label">Способ оплаты</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {METHODS.map((m) => {
              const active = method === m;
              const blocked = isKassaExpense(m) && kassaUnavailable && !(expense && isKassaExpense(expense.method));
              return (
                <button
                  key={m}
                  type="button"
                  disabled={moneyDisabled || blocked}
                  title={blocked ? 'Смен ещё не было' : undefined}
                  onClick={() => {
                    methodTouched.current = true;
                    setMethod(m);
                    // Открытой смены нет — сразу предлагаем последнюю закрытую.
                    if (isKassaExpense(m) && !hasOpenShift && !shiftId && closedOptions[0]) {
                      setShiftId(closedOptions[0].id);
                    }
                  }}
                  className={`px-3 py-2 rounded-md text-sm border transition-colors disabled:opacity-40 ${
                    active
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {EXPENSE_METHOD_LABELS[m]}
                </button>
              );
            })}
          </div>
          {kassa && !locked && (
            <div className="mt-3">
              <label className="label">Смена</label>
              <select className="input" value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
                {hasOpenShift ? (
                  <option value="">Текущая · №{current?.shift?.number} (открыта)</option>
                ) : (
                  <option value="" disabled>Открытой смены нет</option>
                )}
                {closedOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    №{s.number} · закрыта {s.closedAt ? formatDate(s.closedAt) : '—'}
                    {diffLabel(s.cashDiff)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {kassa && !locked && !shiftId && current?.shift && (
            <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
              <Wallet size={13} className="text-slate-400" />
              {method === 'KASSA_CASH'
                ? `Спишется из кассы смены №${current.shift.number} — сейчас в ящике ${formatRub(cashInDrawer)}`
                : `Пройдёт по смене №${current.shift.number}, наличность не тронет`}
            </div>
          )}
          {/* Частая ошибка: аренду платят из сейфа, но выбирают «с кассы» —
              тогда смена уходит в минус. Не блокируем, но говорим прямо. */}
          {method === 'KASSA_CASH' && !locked && !shiftId && hasOpenShift && overdrawn && (
            <div className="text-xs text-amber-600 mt-1">
              Это больше, чем есть в ящике: ожидаемая наличность станет{' '}
              {formatRub(cashInDrawer - amountKop)}. Если платили не из кассы,
              выберите «Наличные».
            </div>
          )}
          {kassa && !locked && pickedClosed && (
            <div className="text-xs text-amber-800 mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
              Смена №{pickedClosed.number} уже закрыта. Расход попадёт в её отчёт
              {method === 'KASSA_CASH' ? ' и уменьшит ожидаемую наличность' : ''}; на бумажном
              Z-отчёте его не будет.
              {method === 'KASSA_CASH' && pickedClosed.cashDiff != null && amountKop > 0 && (
                <> Расхождение станет <b>{formatSignedRub(projectedDiff(pickedClosed, amountKop, expense))}</b>.</>
              )}
            </div>
          )}
          {!kassa && !hasOpenShift && !locked && (
            <div className="text-xs mt-2 text-slate-500">
              Открытой смены нет — расход «с кассы» можно провести в одну из закрытых смен.
            </div>
          )}
        </div>

        {method === 'OTHER' && (
          <div>
            <label className="label">Название способа</label>
            <input
              className="input"
              list="expense-method-labels"
              value={methodLabel}
              onChange={(e) => setMethodLabel(e.target.value)}
              placeholder="Напр.: перевод с личной карты"
              disabled={moneyDisabled}
            />
            <datalist id="expense-method-labels">
              {labels?.map((l) => <option key={l} value={l} />)}
            </datalist>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Статья расхода</label>
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Без статьи</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value={NEW_CATEGORY}>➕ Новая статья…</option>
            </select>
          </div>
          <div>
            <label className="label">Дата расхода</label>
            <input
              type="date"
              className="input"
              value={spentAt}
              onChange={(e) => setSpentAt(e.target.value)}
              disabled={kassa || moneyDisabled}
              title={kassa ? 'Дата кассового расхода — момент проведения, для закрытой смены — её закрытие' : undefined}
            />
          </div>
        </div>

        {categoryId === NEW_CATEGORY && (
          <div>
            <label className="label">Название новой статьи</label>
            <input
              className="input"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Напр.: Такси"
              autoFocus
            />
          </div>
        )}

        <div>
          <label className="label">Комментарий</label>
          <input
            className="input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="За что заплатили"
          />
        </div>
      </div>
    </Modal>
  );
}

// ─── Статьи расходов ──────────────────────────────────────────────────
function CategoriesTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ id?: string; name: string; color: string | null } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/expense-categories', 'all'],
    queryFn: () => listExpenseCategories({ includeInactive: true }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin/expense-categories'] });
    qc.invalidateQueries({ queryKey: ['admin/expenses'] });
  };

  const saveMut = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('нет формы');
      const body = { name: form.name.trim(), color: form.color };
      return form.id ? updateExpenseCategory(form.id, body) : createExpenseCategory(body);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      invalidate();
    },
  });

  const toggleMut = useMutation({
    mutationFn: (c: ExpenseCategory) => updateExpenseCategory(c.id, { isActive: !c.isActive }),
    onSuccess: invalidate,
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteExpenseCategory(id),
    onSuccess: () => {
      toast.success('Статья удалена');
      invalidate();
    },
  });

  const seedMut = useMutation({
    mutationFn: seedExpenseCategories,
    onSuccess: () => {
      toast.success('Типовые статьи добавлены');
      invalidate();
    },
  });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-500">
          Статьи нужны, чтобы видеть, куда уходят деньги: в аналитике расходы разложатся по ним
        </div>
        <div className="flex gap-2">
          {data?.length === 0 && (
            <button className="btn-outline" disabled={seedMut.isPending} onClick={() => seedMut.mutate()}>
              Добавить типовые
            </button>
          )}
          <button className="btn-primary" onClick={() => setForm({ name: '', color: CATEGORY_COLORS[3] })}>
            <Plus size={16} /> Новая статья
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-right px-4 py-3">Расходов</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((c) => (
                <tr key={c.id} className="table-row">
                  <td className="px-4 py-3">
                    <CategoryTag category={c} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{c.expensesCount}</td>
                  <td className="px-4 py-3">
                    {c.isActive ? <Badge tone="success">Активна</Badge> : <Badge tone="slate">Скрыта</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => toggleMut.mutate(c)}
                        disabled={toggleMut.isPending}
                      >
                        {c.isActive ? 'Скрыть' : 'Вернуть'}
                      </button>
                      <button
                        className="btn-ghost p-1.5"
                        title="Изменить"
                        onClick={() => setForm({ id: c.id, name: c.name, color: c.color })}
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        className="btn-ghost p-1.5 text-rose-600 disabled:opacity-40"
                        title={c.expensesCount > 0 ? 'По статье есть расходы — её можно только скрыть' : 'Удалить'}
                        disabled={c.expensesCount > 0 || delMut.isPending}
                        onClick={() => {
                          if (confirm(`Удалить статью «${c.name}»?`)) delMut.mutate(c.id);
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-400 py-10">
                    Статей пока нет — добавьте типовые или создайте свою
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Изменить статью' : 'Новая статья'}
        size="sm"
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button
              className="btn-primary"
              disabled={!form?.name.trim() || saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              Сохранить
            </button>
          </>
        }
      >
        {form && (
          <div className="grid gap-4">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Напр.: Аренда"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Цвет метки</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${
                      form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'
                    }`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
